import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { usePageContent, getSection } from "@/hooks/usePageContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { 
  ArrowLeft,
  Clock,
  Calendar,
  User,
  Eye,
  Share2,
  BookOpen,
  Tag,
  ChevronRight,
  Loader2,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

const DEFAULT_BLOG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/ff7136f51a33cfcbce14d4090c85bdaaf22de77c4467c8bb6ae9b571998687dd.png';

export const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // CMS Template Strings (Phase D - shared detail template)
  const { content: cmsContent } = usePageContent('blog-detail');
  const t = getSection(cmsContent, 'template_strings', {
    back_label: 'Back to Blog',
    share_label: 'Share this article:',
    related_header: 'Related Articles',
    tags_label: 'Tags:',
    author_header: 'About the Author',
    not_found_title: 'Blog post not found',
    not_found_subtitle: "The blog post you're looking for doesn't exist or has been removed."
  });

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const res = await fetch(`${API_URL}/api/public/blog/${slug}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError("Blog post not found");
          } else {
            throw new Error("Failed to fetch post");
          }
          return;
        }
        
        const data = await res.json();
        setPost(data);
        
        // Fetch related posts from same category
        if (data.category) {
          const relatedRes = await fetch(`${API_URL}/api/public/blog?category=${encodeURIComponent(data.category)}&limit=4`);
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            // Filter out current post
            setRelatedPosts(relatedData.posts.filter(p => p.slug !== slug).slice(0, 3));
          }
        }
      } catch (e) {
        console.error('Failed to fetch blog post:', e);
        setError("Failed to load blog post");
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      copy: url
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <BookOpen className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
          <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            {error || t.not_found_title}
          </h1>
          <p className={`mb-8 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            {t.not_found_subtitle}
          </p>
          <Button onClick={() => navigate('/blog')} className="bg-ux-cyan hover:bg-ux-blue text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back_label}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="blog-detail-page">
      {/* SEO Meta Tags */}
      <SEO 
        title={`${post.title} | UniXperts Blog`}
        description={post.excerpt || post.title}
        keywords={post.tags?.join(', ') || post.category}
        canonical={`https://unixperts.com/blog/${slug}`}
        ogImage={post.featured_image || DEFAULT_BLOG_IMAGE}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.featured_image || DEFAULT_BLOG_IMAGE,
          "author": {
            "@type": "Person",
            "name": post.author_name || "UniXperts Team"
          },
          "publisher": {
            "@type": "Organization",
            "name": "UniXperts",
            "logo": {
              "@type": "ImageObject",
              "url": "https://unixperts.com/logo512.png"
            }
          },
          "datePublished": post.publish_date || post.created_at,
          "dateModified": post.updated_at || post.publish_date,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://unixperts.com/blog/${slug}`
          }
        }}
      />

      {/* Hero Section */}
      <section className="relative">
        <div className="relative h-[50vh] md:h-[60vh]">
          <img 
            src={post.featured_image || DEFAULT_BLOG_IMAGE} 
            alt={post.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = DEFAULT_BLOG_IMAGE; }}
          />
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#1a2830] via-[#1a2830]/70 to-transparent' : 'bg-gradient-to-t from-white via-black/50 to-transparent'}`} />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 pb-8 md:pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-4">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 text-white/50" />
              <Link to="/blog" className="text-white/70 hover:text-white transition-colors">Blog</Link>
              <ChevronRight className="w-4 h-4 text-white/50" />
              <span className="text-white truncate max-w-[200px]">{post.title}</span>
            </nav>
            
            <Badge className="bg-ux-cyan text-white mb-4">
              {post.category || 'Article'}
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-white/80">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-ux-violet flex items-center justify-center text-white font-bold">
                  {(post.author_name || 'U')[0]}
                </div>
                <div>
                  <p className="font-medium text-white">{post.author_name || 'UniXperts Team'}</p>
                  <p className="text-sm text-white/60">{formatDate(post.publish_date || post.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {post.read_time || '5 min read'}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {(post.views || 0).toLocaleString()} views
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className={`py-12 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Excerpt */}
              {post.excerpt && (
                <p className={`text-xl leading-relaxed mb-8 font-medium ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                  {post.excerpt}
                </p>
              )}
              
              {/* Article Content */}
              <article 
                className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}
                dangerouslySetInnerHTML={{ __html: post.content || '<p>No content available.</p>' }}
              />
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-white/10">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
                    {post.tags.map((tag) => (
                      <Badge 
                        key={tag} 
                        variant="outline" 
                        className={`${isDark ? 'border-white/20 text-white/70' : 'border-gray-300 text-gray-600'}`}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Share Section */}
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-white/10">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {t.share_label}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('facebook')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <Facebook className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('twitter')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <Twitter className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('linkedin')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <Linkedin className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('copy')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <LinkIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Back to Blog */}
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/blog')}
                  className={`w-full ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.back_label}
                </Button>
                
                {/* CTA Card */}
                <div className="rounded-2xl p-6 bg-gradient-to-br from-ux-cyan to-ux-violet text-white">
                  <h3 className="font-bold mb-2">Need Guidance?</h3>
                  <p className="text-sm text-white/80 mb-4">
                    Talk to our expert counselors about your study abroad plans.
                  </p>
                  <Button asChild className="w-full bg-white text-ux-violet hover:bg-white/90 text-sm whitespace-nowrap">
                    <Link to="/contact">Free Consultation</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className={`py-16 ${isDark ? 'bg-[#233842]' : 'bg-gray-50'}`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="blog-related-header">
              {t.related_header}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id || relatedPost.slug} 
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <div className="glass-panel rounded-2xl overflow-hidden hover:scale-[1.02] transition-all">
                    <div className="relative h-40">
                      <img 
                        src={relatedPost.featured_image || DEFAULT_BLOG_IMAGE} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => { e.target.src = DEFAULT_BLOG_IMAGE; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <div className="p-4">
                      <Badge className={`mb-2 text-xs ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                        {relatedPost.category}
                      </Badge>
                      <h3 className={`font-bold line-clamp-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {relatedPost.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
